import debounce from 'debounce'
import rafThrottle from 'raf-throttle'
import ClipboardJS from 'clipboard'
import { CountUp } from 'countup.js'

import assetUrl from 'src/utils/assetUrl'

const smokeImage = assetUrl(RAILS_ASSET_URL('/wmtree/Smoke-Element.png'))

let currHeight // The ultimate say in innerHeight for iOS

const getWidth  = () => document.documentElement.clientWidth // Without scrollbar
const getHeight = () => currHeight // window.innerHeight // Full height on iOS
const getAspect = () => getWidth() / getHeight()
const getHeightOverlap = () => getHeight() + 200

const initSmoke = () => {
  const clock           = new THREE.Clock()
  const renderer        = new THREE.WebGLRenderer()
  const scene           = new THREE.Scene()
  const camera          = new THREE.PerspectiveCamera(75, getAspect(), 1, 10000)
  const light           = new THREE.DirectionalLight(0xffddaa, 1)
  const smokeGeo        = new THREE.PlaneGeometry(300, 300)
  const smokeTexture    = new THREE.TextureLoader().load(smokeImage)
  const smokeMaterial   = new THREE.MeshLambertMaterial({
                          color:        0xffffff,
                          map:          smokeTexture,
                          transparent:  true,
                        })
  const smokeParticles  = []
  const smokeContainer  = document.getElementById('smokeContainer')
  const [ smokeSc1, smokeSc2 ] = document.querySelectorAll('.smoke-scroll')
  let playing = false

  const animate = () => {
    const delta = clock.getDelta()
    smokeParticles.forEach(particle => particle.rotation.z += delta * 0.2)
    renderer.render(scene, camera)

    if (playing)
      requestAnimationFrame(animate)
  }

  const handleResize = () => {
    camera.aspect = getAspect()
    camera.updateProjectionMatrix()
    renderer.setSize(getWidth(), getHeightOverlap())
  }

  const handleScroll = () => {
    const height = getHeight()
    const page = window.scrollY / height
    const opacity =
      page >= 2.2 && page < 2.75
    ? (window.scrollY - height * 2.2) / (height * 0.4)
    : page >= 2.75 && page < 2.8
    ? 1
    : page >= 2.8 && page < 3.2
    ? 1 - (window.scrollY - height * 2.8) / (height * 0.4)
    : 0

    if (!playing && opacity)
      requestAnimationFrame(animate)

    playing = !!opacity

    smokeContainer.style.display = playing ? 'block' : 'none'
    smokeContainer.style.pointerEvents = opacity == 1 ? '' : 'none'
    smokeContainer.style.opacity = opacity

    if (page >= 2.75) {
      smokeSc1.style.zIndex = 0
      smokeSc2.style.zIndex = 1
    } else {
      smokeSc1.style.zIndex = 1
      smokeSc2.style.zIndex = 0
    }
  }

  renderer.setSize(getWidth(), getHeightOverlap())
  camera.position.z = 1000
  light.position.set(-1,0,1)
  scene.background = new THREE.Color(0x293129)
  scene.add(camera)
  scene.add(light)

  for (let p = 0; p < 150; p++) {
    const particle = new THREE.Mesh(smokeGeo, smokeMaterial)
    particle.position.set(
      Math.random() * 500 - 250,
      Math.random() * 500 - 250,
      Math.random() * 1000 - 100,
    )
    particle.rotation.z = Math.random() * 360
    scene.add(particle)
    smokeParticles.push(particle)
  }

  smokeContainer.appendChild(renderer.domElement)
  addEventListener('resize', debounce(handleResize, 66))
  addEventListener('scroll', rafThrottle(handleScroll))
  animate()
  handleScroll()
}

document.addEventListener('DOMContentLoaded', initSmoke)

// Instagram feed
const initFeed = () => {
  const feed = document.getElementById('feed')

  const delegateFeedScrollClick = e => {
    let target

    if (target = e.target.closest('.feed-scroll')) {
      const page = parseInt(target.dataset.page)
      feed.scrollBy({ left: getWidth() * page, behavior: 'smooth' })
    }
  }

  document.addEventListener('click', delegateFeedScrollClick)
}

document.addEventListener('DOMContentLoaded', initFeed)

// Fix min-height 100vh on iOS browsers
const initHeightFix = () => {
  const ruler = document.createElement('div')
  ruler.setAttribute('style', `
    pointer-events: none;
    position: fixed;
    height: 100%;
    height: max(100vh, 100%);
  `.replace(/\s+/g, ' ').trim())
  document.body.appendChild(ruler)

  const diffRuler = document.createElement('div')
  diffRuler.setAttribute('style', `
    pointer-events: none;
    position: fixed;
    height: 100vh;
    height: min(100vh, 100%);
  `.replace(/\s+/g, ' ').trim())
  document.body.appendChild(diffRuler)

  let prevWidth = window.innerWidth
  let prevHeight
  currHeight = ruler.offsetHeight

  const handleOrientationChange = () => {
    currHeight = ruler.offsetHeight
    setPageHeight()
  }

  const handleResize = () => {
    const delta = Math.abs(ruler.offsetHeight - currHeight)

    currHeight =
      delta < 140 && prevWidth == window.innerWidth // Probably mobile scroll
    ? Math.max(currHeight, ruler.offsetHeight)
    : ruler.offsetHeight

    prevWidth = window.innerWidth

    setPageHeight()
  }

  const handleScroll = () => {
    currHeight = Math.max(currHeight, ruler.offsetHeight)
    setPageHeight()
  }

  const setPageHeight = () => {
    if (currHeight == prevHeight) return

    document.documentElement.style
    .setProperty('--page-height', `${currHeight}px`)
    document.documentElement.style
    .setProperty('--difference',
      `${Math.abs(ruler.offsetHeight - diffRuler.offsetHeight)}px`)
    prevHeight = currHeight
  }

  addEventListener('orientationchange', handleOrientationChange)
  addEventListener('resize', debounce(handleResize, 66))
  addEventListener('scroll', rafThrottle(handleScroll))

  setPageHeight()
}

initHeightFix()

// Modal for Get Involved section
const initModal = () => {
  const openModal = button => {
    const modal = document.querySelector(button.dataset.target)
    modal.style.display = 'block'
    modal.offsetHeight // force repaint
    modal.style.opacity = 1
    document.documentElement.style.overflow = 'hidden'
  }

  const cleanupModal = e => {
    const modal = e.target
    if (!modal.matches('.modal')) return

    modal.removeEventListener('transitionend', cleanupModal)
    modal.style.display = 'none'
  }

  const closeModal = button => {
    const modal = document.querySelector(button.dataset.target)
    if ('transition' in modal.style) {
      modal.addEventListener('transitionend', cleanupModal)
      modal.style.opacity = 0
    } else
      modal.style.display = 'none'
    document.documentElement.style.overflow = ''
  }

  const delegateModalClick = e => {
    let target

    if (target = e.target.closest('.modal-button'))
      return openModal(target)

    if (target = e.target.closest('.modal-close'))
      return closeModal(target)
  }

  document.addEventListener('click', delegateModalClick)
}

document.addEventListener('DOMContentLoaded', initModal)

// Clipboard
const showTooltip = (btn, message) => {
  $(btn).tooltip('hide')
    .attr('data-original-title', message)
    .tooltip('show')
  setTimeout(() => $(btn).tooltip('hide'), 1000)
}

const initClipboard = () => {
  const btn = '[data-clipboard-target]'
  const clipboard = new ClipboardJS(btn)

  $(btn).tooltip({ trigger: 'click', placement: 'top' })

  clipboard.on('success', function (e) {
    showTooltip(e.trigger, 'Copied!')
    e.clearSelection()
  })

  clipboard.on('error', function(e) {
    const key = navigator.platform === 'MacIntel' ? 'Cmd' : 'Ctrl'
    showTooltip(e.trigger, `Press ${key}+C to copy`)
  })
}

document.addEventListener('DOMContentLoaded', initClipboard)

// Film modal
const initFilmModal = () => {
  const iframe = document.getElementById('modal-film')
  let player

  const initPlayer = button => {
    if (button.dataset.target == '#watch-the-film') {
      player = player || new Vimeo.Player(iframe)
      return true
    } else
      return false
  }

  const playFilm  = button => initPlayer(button) && player.play()
  const pauseFilm = button => initPlayer(button) && player.pause()

  const delegateModalClick = e => {
    let target

    if (target = e.target.closest('.modal-button'))
      return playFilm(target)

    if (target = e.target.closest('.modal-close'))
      return pauseFilm(target)
  }

  document.addEventListener('click', delegateModalClick)
}

document.addEventListener('DOMContentLoaded', initFilmModal)

// Count Up
const initCountUp = () => {
  const element = document.getElementById('count-up')
  const amount  = parseInt(element.textContent.split('.')[0].replace(/\D/g, ''))
  const countUp = new CountUp(element, amount, { startVal: amount * 0.95 })

  if (countUp.error)
    return console.log(countUp.error)

  countUp.start(() => {
    element.classList.add('text-primary')
    element.classList.add('goal-reached')
  })
  element.style.opacity = 1
  element.style.transform = ''
}

document.addEventListener('DOMContentLoaded', initCountUp)
